import { useState } from 'react';
import {
  HelpCircle,
  FileText,
  Calendar,
  BarChart3,
  CreditCard,
  Users,
  CalendarDays,
  CheckCircle,
  Clock,
  XCircle,
  Download,
  Eye,
  UserPlus,
  Settings,
  Mail,
  ChevronDown,
  ChevronRight,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { clsx } from 'clsx';

interface AccordionItemProps {
  title: string;
  children: React.ReactNode;
  icon?: React.ReactNode;
  defaultOpen?: boolean;
}

function AccordionItem({ title, children, icon, defaultOpen = false }: AccordionItemProps) {
  const [isOpen, setIsOpen] = useState(defaultOpen);

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors text-left"
      >
        <div className="flex items-center gap-3">
          {icon && <span className="text-primary-600">{icon}</span>}
          <span className="font-medium text-gray-900">{title}</span>
        </div>
        {isOpen ? (
          <ChevronDown className="h-5 w-5 text-gray-500" />
        ) : (
          <ChevronRight className="h-5 w-5 text-gray-500" />
        )}
      </button>
      {isOpen && (
        <div className="p-4 bg-white border-t border-gray-200">
          {children}
        </div>
      )}
    </div>
  );
}

function EmployeeGuide() {
  return (
    <div className="space-y-4">
      {/* Dashboard */}
      <AccordionItem
        title="Dashboard Overview"
        icon={<BarChart3 className="h-5 w-5" />}
        defaultOpen={true}
      >
        <div className="prose prose-sm max-w-none">
          <p className="text-gray-600">
            Your dashboard provides a quick overview of your leave status and balances:
          </p>
          <ul className="mt-3 space-y-2 text-gray-600">
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span><strong>Leave Balances</strong> - View your current leave balances for all leave types</span>
            </li>
            <li className="flex items-start gap-2">
              <Clock className="h-4 w-4 text-amber-500 mt-0.5 flex-shrink-0" />
              <span><strong>Pending Applications</strong> - See applications awaiting approval</span>
            </li>
            <li className="flex items-start gap-2">
              <Calendar className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
              <span><strong>Upcoming Leave</strong> - View your approved upcoming leave</span>
            </li>
          </ul>
        </div>
      </AccordionItem>

      {/* Applying for Leave */}
      <AccordionItem
        title="Applying for Leave"
        icon={<FileText className="h-5 w-5" />}
      >
        <div className="prose prose-sm max-w-none">
          <p className="text-gray-600 mb-4">
            Follow these steps to submit a leave application:
          </p>

          <div className="space-y-4">
            <div className="bg-primary-50 rounded-lg p-4">
              <h4 className="font-semibold text-primary-900 mb-2">Step 1: Navigate to My Leave</h4>
              <p className="text-primary-700 text-sm">
                Click on "My Leave" in the sidebar to access your leave applications.
              </p>
            </div>

            <div className="bg-primary-50 rounded-lg p-4">
              <h4 className="font-semibold text-primary-900 mb-2">Step 2: Click "Apply for Leave"</h4>
              <p className="text-primary-700 text-sm">
                Click the blue "Apply for Leave" button to start a new application.
              </p>
            </div>

            <div className="bg-primary-50 rounded-lg p-4">
              <h4 className="font-semibold text-primary-900 mb-2">Step 3: Select Leave Type</h4>
              <p className="text-primary-700 text-sm">
                Choose from: Annual Leave, Sick Leave, Family Responsibility, Maternity/Paternity, Study Leave, or Unpaid Leave.
              </p>
            </div>

            <div className="bg-primary-50 rounded-lg p-4">
              <h4 className="font-semibold text-primary-900 mb-2">Step 4: Select Dates</h4>
              <p className="text-primary-700 text-sm">
                Pick your start and end dates. The system automatically excludes weekends and public holidays.
              </p>
            </div>

            <div className="bg-primary-50 rounded-lg p-4">
              <h4 className="font-semibold text-primary-900 mb-2">Step 5: Add Details (if required)</h4>
              <p className="text-primary-700 text-sm">
                Some leave types require a reason or supporting documents (e.g., medical certificate for sick leave over 2 days).
              </p>
            </div>

            <div className="bg-primary-50 rounded-lg p-4">
              <h4 className="font-semibold text-primary-900 mb-2">Step 6: Submit</h4>
              <p className="text-primary-700 text-sm">
                Review your application and click "Submit". You'll receive email notifications as your application progresses.
              </p>
            </div>
          </div>

          <div className="mt-4 p-4 bg-amber-50 border border-amber-200 rounded-lg">
            <h4 className="font-semibold text-amber-800 mb-1">Important Notes</h4>
            <ul className="text-amber-700 text-sm space-y-1">
              <li>- Annual leave must be applied for in advance</li>
              <li>- Sick leave can be applied retrospectively</li>
              <li>- Check your balance before applying</li>
            </ul>
          </div>
        </div>
      </AccordionItem>

      {/* Leave Balances */}
      <AccordionItem
        title="Understanding Your Leave Balances"
        icon={<BarChart3 className="h-5 w-5" />}
      >
        <div className="prose prose-sm max-w-none">
          <p className="text-gray-600 mb-4">
            Navigate to "Balances" in the sidebar to view your leave entitlements:
          </p>

          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-2 text-left font-semibold text-gray-900">Leave Type</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-900">Annual Allocation</th>
                  <th className="px-4 py-2 text-left font-semibold text-gray-900">Notes</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                <tr>
                  <td className="px-4 py-2 text-gray-900">Annual Leave</td>
                  <td className="px-4 py-2 text-gray-600">24 days</td>
                  <td className="px-4 py-2 text-gray-500">Allocated upfront each year</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 text-gray-900">Sick Leave</td>
                  <td className="px-4 py-2 text-gray-600">36 days per 3-year cycle</td>
                  <td className="px-4 py-2 text-gray-500">Medical certificate required for 2+ consecutive days</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 text-gray-900">Family Responsibility</td>
                  <td className="px-4 py-2 text-gray-600">3 days</td>
                  <td className="px-4 py-2 text-gray-500">Child illness, family emergencies</td>
                </tr>
                <tr>
                  <td className="px-4 py-2 text-gray-900">Maternity Leave</td>
                  <td className="px-4 py-2 text-gray-600">4 months</td>
                  <td className="px-4 py-2 text-gray-500">As per policy</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="mt-4 flex items-start gap-2 p-3 bg-blue-50 rounded-lg">
            <HelpCircle className="h-5 w-5 text-blue-500 flex-shrink-0 mt-0.5" />
            <p className="text-blue-700 text-sm">
              <strong>Hourly Leave:</strong> You can take leave in hours. Every 8 hours taken equals 1 full day deducted from your balance.
            </p>
          </div>
        </div>
      </AccordionItem>

      {/* Viewing Payslips */}
      <AccordionItem
        title="Viewing Your Payslips"
        icon={<CreditCard className="h-5 w-5" />}
      >
        <div className="prose prose-sm max-w-none">
          <p className="text-gray-600 mb-4">
            Access your monthly payslips from the "Payslips" section:
          </p>

          <div className="space-y-3">
            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="bg-primary-100 rounded-full p-2">
                <Eye className="h-4 w-4 text-primary-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">View Payslips</h4>
                <p className="text-gray-600 text-sm">Click on any month to view your payslip for that period.</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="bg-primary-100 rounded-full p-2">
                <Download className="h-4 w-4 text-primary-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Download Payslips</h4>
                <p className="text-gray-600 text-sm">Download your payslip as a PDF for your records.</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
              <div className="bg-primary-100 rounded-full p-2">
                <Calendar className="h-4 w-4 text-primary-600" />
              </div>
              <div>
                <h4 className="font-medium text-gray-900">Historical Payslips</h4>
                <p className="text-gray-600 text-sm">Use the year selector to view payslips from previous years.</p>
              </div>
            </div>
          </div>

          <div className="mt-4 p-3 bg-green-50 border border-green-200 rounded-lg">
            <p className="text-green-700 text-sm">
              <strong>Email Notification:</strong> You'll receive an email notification when a new payslip is uploaded.
            </p>
          </div>
        </div>
      </AccordionItem>

      {/* Calendar */}
      <AccordionItem
        title="Using the Calendar"
        icon={<Calendar className="h-5 w-5" />}
      >
        <div className="prose prose-sm max-w-none">
          <p className="text-gray-600 mb-4">
            The calendar view shows leave across the organization:
          </p>

          <ul className="space-y-2 text-gray-600">
            <li className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-green-500"></span>
              <span><strong>Green</strong> - Approved leave</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-amber-500"></span>
              <span><strong>Amber</strong> - Pending approval</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-red-400"></span>
              <span><strong>Red border</strong> - Public holidays</span>
            </li>
          </ul>

          <p className="mt-4 text-gray-600">
            Use the calendar to check team availability before planning your leave.
          </p>
        </div>
      </AccordionItem>

      {/* Profile */}
      <AccordionItem
        title="Managing Your Profile"
        icon={<Settings className="h-5 w-5" />}
      >
        <div className="prose prose-sm max-w-none">
          <p className="text-gray-600 mb-4">
            Update your personal information from the "Profile" section:
          </p>

          <ul className="space-y-2 text-gray-600">
            <li>- Update your contact phone number</li>
            <li>- View your employee details</li>
            <li>- See your department and supervisor information</li>
          </ul>

          <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
            <p className="text-blue-700 text-sm">
              <strong>Note:</strong> Some details like your name, email, and department can only be changed by an administrator.
            </p>
          </div>
        </div>
      </AccordionItem>
    </div>
  );
}

function AdminGuide() {
  return (
    <div className="space-y-4">
      {/* User Management */}
      <AccordionItem
        title="Managing Users"
        icon={<Users className="h-5 w-5" />}
        defaultOpen={true}
      >
        <div className="prose prose-sm max-w-none">
          <p className="text-gray-600 mb-4">
            Navigate to <strong>Administration &gt; Manage Users</strong> to manage employee accounts:
          </p>

          <div className="space-y-4">
            <div className="border-l-4 border-primary-500 pl-4">
              <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                <UserPlus className="h-4 w-4 text-primary-600" />
                Adding New Users
              </h4>
              <ol className="mt-2 text-gray-600 text-sm space-y-1 list-decimal list-inside">
                <li>Click "Add User" button</li>
                <li>Enter employee email address</li>
                <li>Fill in personal details (name, phone, ID number)</li>
                <li>Select department and role</li>
                <li>Assign supervisor (for approval workflow)</li>
                <li>Click "Create User"</li>
              </ol>
              <p className="mt-2 text-sm text-gray-500">
                The employee will receive an email invitation to set their password.
              </p>
            </div>

            <div className="border-l-4 border-amber-500 pl-4">
              <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                <Settings className="h-4 w-4 text-amber-600" />
                Editing User Details
              </h4>
              <p className="mt-2 text-gray-600 text-sm">
                Click on any user row to edit their information, change their role, or update their supervisor.
              </p>
            </div>

            <div className="border-l-4 border-red-500 pl-4">
              <h4 className="font-semibold text-gray-900 flex items-center gap-2">
                <XCircle className="h-4 w-4 text-red-600" />
                Deactivating Users
              </h4>
              <p className="mt-2 text-gray-600 text-sm">
                Toggle the "Active" status to deactivate users who have left. Deactivated users cannot log in but their historical data is preserved.
              </p>
            </div>
          </div>

          <div className="mt-4 p-4 bg-gray-50 rounded-lg">
            <h4 className="font-semibold text-gray-900 mb-2">User Roles</h4>
            <ul className="text-sm text-gray-600 space-y-1">
              <li><strong>EMPLOYEE</strong> - Standard employee access</li>
              <li><strong>SUPERVISOR</strong> - Can recommend leave for their team</li>
              <li><strong>DIRECTOR</strong> - Final leave approval, admin access</li>
              <li><strong>ADMIN</strong> - Full system administration</li>
              <li><strong>BOARD</strong> - Board members (for Director leave approval)</li>
            </ul>
          </div>
        </div>
      </AccordionItem>

      {/* Payslip Management */}
      <AccordionItem
        title="Uploading Payslips"
        icon={<CreditCard className="h-5 w-5" />}
      >
        <div className="prose prose-sm max-w-none">
          <p className="text-gray-600 mb-4">
            Navigate to <strong>Administration &gt; Manage Payslips</strong> to upload monthly payslips:
          </p>

          <div className="bg-primary-50 rounded-lg p-4 mb-4">
            <h4 className="font-semibold text-primary-900 mb-2">Quick Upload Method (Recommended)</h4>
            <p className="text-primary-700 text-sm mb-3">
              Upload a combined PDF containing all employee payslips:
            </p>
            <ol className="text-primary-700 text-sm space-y-2 list-decimal list-inside">
              <li><strong>Select Month/Year</strong> - Choose the payslip period from the dropdowns</li>
              <li><strong>Upload Combined PDF</strong> - Click "Select PDF File" and choose the file</li>
              <li><strong>Assign Pages</strong> - For each page, select the employee from the dropdown</li>
              <li><strong>Process</strong> - Click "Process & Upload" to split and send payslips</li>
            </ol>
          </div>

          <div className="flex items-start gap-2 p-3 bg-green-50 rounded-lg">
            <Mail className="h-5 w-5 text-green-600 flex-shrink-0 mt-0.5" />
            <p className="text-green-700 text-sm">
              <strong>Automatic Notifications:</strong> Each employee automatically receives an email notification when their payslip is uploaded.
            </p>
          </div>

          <div className="mt-4">
            <h4 className="font-semibold text-gray-900 mb-2">Status Dashboard</h4>
            <p className="text-gray-600 text-sm">
              The dashboard shows upload progress for the selected month:
            </p>
            <ul className="mt-2 text-sm text-gray-600 space-y-1">
              <li><strong>Total Employees</strong> - Number of active employees</li>
              <li><strong>Uploaded</strong> - Payslips successfully uploaded</li>
              <li><strong>Pending</strong> - Employees still missing payslips</li>
              <li><strong>Completion</strong> - Percentage complete</li>
            </ul>
          </div>

          <div className="mt-4 p-3 bg-amber-50 border border-amber-200 rounded-lg">
            <h4 className="font-semibold text-amber-800 mb-1">Tips</h4>
            <ul className="text-amber-700 text-sm space-y-1">
              <li>- Select the correct month/year before uploading</li>
              <li>- You can upload historical payslips by selecting past months</li>
              <li>- The employee list shows who has already received their payslip</li>
            </ul>
          </div>
        </div>
      </AccordionItem>

      {/* Holiday Management */}
      <AccordionItem
        title="Managing Public Holidays"
        icon={<CalendarDays className="h-5 w-5" />}
      >
        <div className="prose prose-sm max-w-none">
          <p className="text-gray-600 mb-4">
            Navigate to <strong>Administration &gt; Manage Holidays</strong> to configure public holidays:
          </p>

          <div className="space-y-3">
            <div className="border-l-4 border-primary-500 pl-4">
              <h4 className="font-semibold text-gray-900">Adding Holidays</h4>
              <p className="mt-1 text-gray-600 text-sm">
                Click "Add Holiday" to add a new public holiday. Enter the name, date, and whether it's a recurring annual holiday.
              </p>
            </div>

            <div className="border-l-4 border-amber-500 pl-4">
              <h4 className="font-semibold text-gray-900">Standard SA Public Holidays</h4>
              <p className="mt-1 text-gray-600 text-sm">
                The system comes pre-loaded with South African public holidays. These are automatically excluded when calculating leave days.
              </p>
            </div>
          </div>

          <div className="mt-4">
            <h4 className="font-semibold text-gray-900 mb-2">Standard Holidays Include:</h4>
            <div className="grid grid-cols-2 gap-2 text-sm text-gray-600">
              <div>- New Year's Day (1 Jan)</div>
              <div>- Human Rights Day (21 Mar)</div>
              <div>- Good Friday (variable)</div>
              <div>- Family Day (variable)</div>
              <div>- Freedom Day (27 Apr)</div>
              <div>- Workers' Day (1 May)</div>
              <div>- Youth Day (16 Jun)</div>
              <div>- National Women's Day (9 Aug)</div>
              <div>- Heritage Day (24 Sep)</div>
              <div>- Day of Reconciliation (16 Dec)</div>
              <div>- Christmas Day (25 Dec)</div>
              <div>- Day of Goodwill (26 Dec)</div>
            </div>
          </div>
        </div>
      </AccordionItem>

      {/* Leave Approval */}
      <AccordionItem
        title="Approving Leave Applications"
        icon={<CheckCircle className="h-5 w-5" />}
      >
        <div className="prose prose-sm max-w-none">
          <p className="text-gray-600 mb-4">
            Leave applications appear on your dashboard when action is required:
          </p>

          <div className="space-y-4">
            <div className="bg-blue-50 rounded-lg p-4">
              <h4 className="font-semibold text-blue-900 mb-2">Approval Workflow</h4>
              <ol className="text-blue-700 text-sm space-y-2 list-decimal list-inside">
                <li><strong>Employee Submits</strong> - Application created</li>
                <li><strong>Supervisor Recommends</strong> (if applicable) - Recommends approval/decline</li>
                <li><strong>Director Approves</strong> - Final decision</li>
                <li><strong>Leave Deducted</strong> - Balance updated on approval</li>
              </ol>
            </div>

            <div className="flex items-start gap-2 p-3 bg-gray-50 rounded-lg">
              <HelpCircle className="h-5 w-5 text-gray-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-gray-600">
                <p><strong>Direct Reports to Director:</strong> Some employees report directly to the Director. Their applications skip the supervisor step.</p>
              </div>
            </div>

            <div className="flex items-start gap-2 p-3 bg-secondary-50 rounded-lg">
              <HelpCircle className="h-5 w-5 text-secondary-500 flex-shrink-0 mt-0.5" />
              <div className="text-sm text-secondary-700">
                <p><strong>Director's Leave:</strong> When the Director applies for leave, it goes to the Board Chairperson or Vice-Chairperson for approval.</p>
              </div>
            </div>
          </div>

          <div className="mt-4">
            <h4 className="font-semibold text-gray-900 mb-2">Taking Action</h4>
            <p className="text-gray-600 text-sm">
              Click on any pending application to view details and take action:
            </p>
            <ul className="mt-2 text-sm text-gray-600 space-y-1">
              <li><strong>Approve</strong> - Accept the leave request (balance deducted)</li>
              <li><strong>Decline</strong> - Reject with a reason (balance unchanged)</li>
              <li><strong>Request Changes</strong> - Ask employee to modify dates</li>
            </ul>
          </div>
        </div>
      </AccordionItem>

      {/* Reports */}
      <AccordionItem
        title="Reports and Analytics"
        icon={<BarChart3 className="h-5 w-5" />}
      >
        <div className="prose prose-sm max-w-none">
          <p className="text-gray-600 mb-4">
            Use the dashboard and reports to monitor leave patterns:
          </p>

          <ul className="space-y-2 text-gray-600 text-sm">
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span><strong>Leave Usage Summary</strong> - See total leave taken by type across the organization</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span><strong>Department Overview</strong> - Compare leave usage across departments</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span><strong>Balance Tracking</strong> - Monitor employees with low or negative balances</span>
            </li>
            <li className="flex items-start gap-2">
              <CheckCircle className="h-4 w-4 text-green-500 mt-0.5 flex-shrink-0" />
              <span><strong>Sick Leave Patterns</strong> - Identify potential patterns (Monday/Friday sick days)</span>
            </li>
          </ul>
        </div>
      </AccordionItem>
    </div>
  );
}

export default function HelpPage() {
  const { user } = useAuth();
  const isAdmin = user?.role && ['ADMIN', 'DIRECTOR'].includes(user.role);
  const [activeTab, setActiveTab] = useState<'employee' | 'admin'>(isAdmin ? 'admin' : 'employee');

  return (
    <div className="space-y-6">
      {/* Header with HCFS branding */}
      <div className="bg-gradient-to-r from-primary-600 to-primary-700 rounded-xl p-6 text-white">
        <div className="flex items-center gap-4">
          <div className="bg-white/20 rounded-full p-3">
            <HelpCircle className="h-8 w-8" />
          </div>
          <div>
            <h1 className="text-2xl font-bold">Help & User Guide</h1>
            <p className="text-primary-100 mt-1">
              Learn how to use PeopleMX - HCFS Leave & Payslip Management System
            </p>
          </div>
        </div>
      </div>

      {/* Quick Links */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <a href="/leave" className="card p-4 hover:shadow-md transition-shadow group">
          <FileText className="h-6 w-6 text-primary-600 group-hover:scale-110 transition-transform" />
          <h3 className="font-medium text-gray-900 mt-2">My Leave</h3>
          <p className="text-sm text-gray-500">Apply for leave</p>
        </a>
        <a href="/balances" className="card p-4 hover:shadow-md transition-shadow group">
          <BarChart3 className="h-6 w-6 text-primary-600 group-hover:scale-110 transition-transform" />
          <h3 className="font-medium text-gray-900 mt-2">Balances</h3>
          <p className="text-sm text-gray-500">Check your leave balance</p>
        </a>
        <a href="/payslips" className="card p-4 hover:shadow-md transition-shadow group">
          <CreditCard className="h-6 w-6 text-primary-600 group-hover:scale-110 transition-transform" />
          <h3 className="font-medium text-gray-900 mt-2">Payslips</h3>
          <p className="text-sm text-gray-500">View your payslips</p>
        </a>
        <a href="/calendar" className="card p-4 hover:shadow-md transition-shadow group">
          <Calendar className="h-6 w-6 text-primary-600 group-hover:scale-110 transition-transform" />
          <h3 className="font-medium text-gray-900 mt-2">Calendar</h3>
          <p className="text-sm text-gray-500">View leave calendar</p>
        </a>
      </div>

      {/* Tab Navigation */}
      {isAdmin && (
        <div className="border-b border-gray-200">
          <nav className="flex space-x-8">
            <button
              onClick={() => setActiveTab('employee')}
              className={clsx(
                'py-4 px-1 border-b-2 font-medium text-sm transition-colors',
                activeTab === 'employee'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              )}
            >
              Employee Guide
            </button>
            <button
              onClick={() => setActiveTab('admin')}
              className={clsx(
                'py-4 px-1 border-b-2 font-medium text-sm transition-colors',
                activeTab === 'admin'
                  ? 'border-primary-500 text-primary-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              )}
            >
              Administrator Guide
            </button>
          </nav>
        </div>
      )}

      {/* Content */}
      <div className="card p-6">
        {!isAdmin && (
          <div className="mb-6">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <FileText className="h-5 w-5 text-primary-600" />
              Employee Guide
            </h2>
            <p className="text-gray-500 text-sm mt-1">
              Everything you need to know about using PeopleMX
            </p>
          </div>
        )}

        {activeTab === 'employee' ? <EmployeeGuide /> : <AdminGuide />}
      </div>

      {/* Support Contact */}
      <div className="card p-6 bg-gray-50">
        <div className="flex items-start gap-4">
          <div className="bg-secondary-100 rounded-full p-3">
            <Mail className="h-6 w-6 text-secondary-600" />
          </div>
          <div>
            <h3 className="font-semibold text-gray-900">Need More Help?</h3>
            <p className="text-gray-600 mt-1">
              If you have questions or need assistance, please contact:
            </p>
            <ul className="mt-2 text-sm text-gray-600">
              <li><strong>Administration:</strong> Elzadia Buys</li>
              <li><strong>Technical Support:</strong> admin@hcfs.co.za</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
}
